/**
 * Student の t 分布。
 *
 * 平均の比較では母分散が分からないため、標本から推定した分散を使う。
 * その分だけ裾が厚くなり、少数サンプルで正規分布を当てると
 * p 値も信頼区間も実際より狭く出る。
 *
 * 分布関数は正則化不完全ベータ関数で表せるので、
 * Numerical Recipes の連分数展開を実装している。
 */

import { logGamma } from './chisquare'

const ITMAX = 300
const EPS = 3e-16
const FPMIN = Number.MIN_VALUE / EPS

/**
 * 正則化不完全ベータ関数 I_x(a, b) の連分数部分。
 * 収束が速い側（x < (a+1)/(a+b+2)）で使い、反対側は対称性で移す。
 */
function betaContinuedFraction(a: number, b: number, x: number): number {
  const qab = a + b
  const qap = a + 1
  const qam = a - 1
  let c = 1
  let d = 1 - (qab * x) / qap
  if (Math.abs(d) < FPMIN) d = FPMIN
  d = 1 / d
  let h = d

  for (let m = 1; m <= ITMAX; m++) {
    const m2 = 2 * m
    // 偶数項
    let aa = (m * (b - m) * x) / ((qam + m2) * (a + m2))
    d = 1 + aa * d
    if (Math.abs(d) < FPMIN) d = FPMIN
    c = 1 + aa / c
    if (Math.abs(c) < FPMIN) c = FPMIN
    d = 1 / d
    h *= d * c
    // 奇数項
    aa = (-(a + m) * (qab + m) * x) / ((a + m2) * (qap + m2))
    d = 1 + aa * d
    if (Math.abs(d) < FPMIN) d = FPMIN
    c = 1 + aa / c
    if (Math.abs(c) < FPMIN) c = FPMIN
    d = 1 / d
    const del = d * c
    h *= del
    if (Math.abs(del - 1) < EPS) break
  }
  return h
}

/** 正則化不完全ベータ関数 I_x(a, b)。 */
export function incompleteBeta(a: number, b: number, x: number): number {
  if (!(x >= 0) || !(x <= 1)) return NaN
  if (x === 0) return 0
  if (x === 1) return 1

  const front = Math.exp(
    logGamma(a + b) -
      logGamma(a) -
      logGamma(b) +
      a * Math.log(x) +
      b * Math.log(1 - x),
  )
  // 連分数は x が (a+1)/(a+b+2) より小さいときに速く収束する
  return x < (a + 1) / (a + b + 2)
    ? (front * betaContinuedFraction(a, b, x)) / a
    : 1 - (front * betaContinuedFraction(b, a, 1 - x)) / b
}

/** 自由度 df の t 分布の累積分布関数 P(T <= t)。 */
export function studentTCdf(t: number, df: number): number {
  if (!Number.isFinite(t) || !(df > 0)) return NaN
  const half = incompleteBeta(df / 2, 0.5, df / (df + t * t)) / 2
  return t >= 0 ? 1 - half : half
}

/**
 * 自由度 df の t 分布の上側確率 P(T > t)。
 * 1 - cdf との差は打ち切り誤差だけだが、右裾で桁落ちしないようこちらを使う。
 */
export function studentTSf(t: number, df: number): number {
  return studentTCdf(-t, df)
}

/** 両側の p 値 P(|T| > |t|)。 */
export function studentTTwoSided(t: number, df: number): number {
  if (!Number.isFinite(t) || !(df > 0)) return NaN
  return incompleteBeta(df / 2, 0.5, df / (df + t * t))
}

/**
 * 自由度 df の t 分布の分位点関数。
 *
 * 累積分布関数が単調なので二分探索で解く。有理近似を持ち込むより、
 * すでに高精度な累積分布関数を反転するほうが誤差を追いやすい。
 */
export function studentTQuantile(p: number, df: number): number {
  if (!(df > 0) || !Number.isFinite(p)) return NaN
  if (p <= 0) return p === 0 ? -Infinity : NaN
  if (p >= 1) return p === 1 ? Infinity : NaN
  if (p === 0.5) return 0

  let low = -1
  let high = 1
  // 解を含むまで区間を広げる。自由度が小さいと裾が非常に重く、
  // 自由度 1 で p = 1 − 1e-15 なら分位点は 3e14 に達する。逐次確認では
  // 有意水準が 1e-5 台まで下がるため、上限を小さく取ると
  // 区間が実際より狭い値で頭打ちになる。
  while (studentTCdf(low, df) > p && low > -1e300) low *= 2
  while (studentTCdf(high, df) < p && high < 1e300) high *= 2

  for (let i = 0; i < 200; i++) {
    const mid = (low + high) / 2
    if (studentTCdf(mid, df) < p) low = mid
    else high = mid
    if (high - low < Math.abs(high) * 1e-15 + 1e-15) break
  }
  return (low + high) / 2
}
